import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Clock, Loader, Trash2, X } from 'lucide-react';
import { Z } from '../constants/tokens';

function ToastDuration({ startedAt, endedAt }) {
  const [elapsed, setElapsed] = React.useState(
    endedAt ? null : Math.floor((Date.now() - startedAt) / 1000)
  );
  useEffect(() => {
    if (endedAt) return;
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [startedAt, endedAt]);

  const secs = endedAt
    ? Math.round((endedAt - startedAt) / 1000)
    : elapsed ?? 0;

  const fmt = (s) => s >= 3600
    ? `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`
    : s >= 60 ? `${Math.floor(s/60)}m ${s%60}s` : `${s}s`;

  return (
    <span style={{ fontSize: '0.65rem', opacity: 0.55, fontFamily: 'monospace',
                   flexShrink: 0, alignSelf: 'center', marginLeft: '4px' }}>
      {endedAt ? fmt(secs) : `${fmt(secs)}…`}
    </span>
  );
}

// Visually hidden but present to assistive tech — the pattern for a live
// region that announces without altering the visual layout.
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

// §03 fix: the toast UI is a collapsed badge, so nothing was ever announced to a
// screen reader — a "saved" confirmation or an error passed silently. These two
// hidden regions mirror the latest message: errors are assertive (interrupt),
// everything else polite. 'running' progress is excluded so it does not spam.
function ToastLiveRegions({ toasts }) {
  const rev = [...toasts].reverse();
  const latestError = rev.find(t => t.type === 'error');
  const latestStatus = rev.find(t => t.type !== 'error' && t.type !== 'running');
  return (
    <>
      <div role="alert" aria-live="assertive" style={SR_ONLY}>{latestError ? latestError.message : ''}</div>
      <div role="status" aria-live="polite" style={SR_ONLY}>{latestStatus ? latestStatus.message : ''}</div>
    </>
  );
}

export default function ToastBar({ toasts, onDismiss, history = [], onClearHistory }) {
  const [showHistory, setShowHistory] = useState(false);

  // Errors stay until the user dismisses them. Successes and info notices are
  // transient, but a failure the user looked away from must not disappear on
  // its own — these carry things like "access change NOT saved" and failed
  // merges, and they replaced blocking window.alert() calls that could not be
  // missed. 'running' toasts are cleared by whatever completes them.
  //
  // The countdowns live in a ref keyed by toast id rather than being rebuilt
  // from scratch on every `toasts` change: a running job re-pushes its progress
  // toast every few seconds, and recreating the timers each time restarted the
  // 6s countdown of every OTHER toast, so a completion notice could sit pinned
  // in the header for as long as the job kept talking.
  const dismissTimers = useRef(new Map());
  useEffect(() => {
    const timers = dismissTimers.current;
    const eligible = new Set(
      toasts.filter(t => t.type !== 'running' && t.type !== 'error').map(t => t.id)
    );
    // Forget timers whose toast is gone (dismissed) or is no longer transient.
    for (const [id, handle] of timers) {
      if (!eligible.has(id)) { clearTimeout(handle); timers.delete(id); }
    }
    // Start a countdown for anything eligible that doesn't have one yet. Note
    // this is "has no timer", not "is a new id": a job's toast keeps its id
    // when it flips running → success, and that transition must start a timer.
    eligible.forEach(id => {
      if (timers.has(id)) return;
      timers.set(id, setTimeout(() => { timers.delete(id); onDismiss(id); }, 6000));
    });
  }, [toasts, onDismiss]);

  useEffect(() => {
    const timers = dismissTimers.current;
    return () => { timers.forEach(clearTimeout); timers.clear(); };
  }, []);

  const cfgFor = (type) => ({
    running: { bg: '#1a1a2e', border: '#7c3aed', color: '#a78bfa' },
    success: { bg: '#052e16', border: '#22c55e', color: '#4ade80' },
    error:   { bg: '#2d0a0a', border: '#ef4444', color: '#f87171' },
    info:    { bg: '#1a1a2e', border: '#3b82f6', color: '#60a5fa' },
  }[type] || { bg: '#1a1a1a', border: '#555', color: '#ccc' });

  const fmtTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const latestToast = toasts.length > 0 ? toasts[toasts.length - 1] : null;
  const badgeCount = toasts.length;

  if (!toasts.length && !history.length) return null;

  // Render a compact badge in the header anchor, with dropdown below
  const anchor = document.getElementById('toast-anchor');
  if (!anchor) {
    // Fallback: bottom-right fixed position
    return ReactDOM.createPortal(
      <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: Z.toast,
                    display: 'flex', flexDirection: 'column', gap: '10px',
                    alignItems: 'flex-end', pointerEvents: 'none' }}>
        <ToastLiveRegions toasts={toasts} />
        {toasts.map(t => {
          const cfg = cfgFor(t.type);
          return (
            <div key={t.id} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`,
              borderRadius: '10px', padding: '12px 18px',
              display: 'flex', alignItems: 'center', gap: '10px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', maxWidth: '380px', pointerEvents: 'all' }}>
              <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                {t.type === 'running'
                  ? <Loader size={14} strokeWidth={2} style={{ color: cfg.color, animation: 'spin 1s linear infinite' }} />
                  : t.type === 'success'
                    ? <Check size={14} strokeWidth={2.5} style={{ color: cfg.color }} />
                    : t.type === 'error'
                      ? <AlertTriangle size={14} strokeWidth={2} style={{ color: cfg.color }} />
                      : <Clock size={14} style={{ color: cfg.color }} />}
              </span>
              {/* pre-wrap so multi-line messages survive. Several of these came
                  from window.alert(), where line breaks separated things like
                  shell commands that must not run together on one line. */}
              <span style={{ fontSize: '0.78rem', color: cfg.color, lineHeight: 1.45, flex: 1, whiteSpace: 'pre-wrap' }}>{t.message}</span>
              {t.startedAt && <ToastDuration startedAt={t.startedAt} endedAt={t.endedAt ?? null} />}
              <button onClick={() => onDismiss(t.id)} aria-label="Dismiss notification" title="Dismiss notification"
                style={{ background: 'none', border: 'none', color: cfg.color, cursor: 'pointer',
                         opacity: 0.6, padding: 0, lineHeight: 1, flexShrink: 0 }}>
                <X size={13} strokeWidth={2} />
              </button>
            </div>
          );
        })}
      </div>,
      document.body
    );
  }

  // ── Top-bar mode: compact badge + dropdown ──
  const latestCfg = latestToast ? cfgFor(latestToast.type) : cfgFor('info');

  return ReactDOM.createPortal(
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <ToastLiveRegions toasts={toasts} />

      {/* Compact badge — shows latest toast message + count */}
      <div
        onClick={() => setShowHistory(v => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={showHistory}
        onKeyDown={(e) => {
          // Only the badge itself toggles: the nested dismiss button gets its
          // own Enter/Space, and it must not also open the history dropdown.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowHistory(v => !v); }
        }}
        onFocus={e => { if (e.target !== e.currentTarget) return; e.currentTarget.style.outline = `2px solid ${latestCfg.border}`; e.currentTarget.style.outlineOffset = '2px'; }}
        onBlur={e => { e.currentTarget.style.outline = 'none'; }}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
          background: latestCfg.bg, border: `1px solid ${latestCfg.border}`,
          borderRadius: '8px', padding: '4px 10px', maxWidth: '360px',
          whiteSpace: 'nowrap', overflow: 'hidden',
        }}
      >
        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {latestToast?.type === 'running'
            ? <Loader size={11} strokeWidth={2} style={{ color: latestCfg.color, animation: 'spin 1s linear infinite' }} />
            : latestToast?.type === 'success'
              ? <Check size={11} strokeWidth={2.5} style={{ color: latestCfg.color }} />
              : latestToast?.type === 'error'
                ? <AlertTriangle size={11} strokeWidth={2} style={{ color: latestCfg.color }} />
                : <Clock size={11} style={{ color: latestCfg.color }} />}
        </span>
        <span style={{ fontSize: '0.68rem', color: latestCfg.color, overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
          {latestToast ? latestToast.message : `${history.length} notifications`}
        </span>
        {latestToast?.startedAt && latestToast?.type === 'running' && (
          <ToastDuration startedAt={latestToast.startedAt} endedAt={null} />
        )}
        {badgeCount > 1 && (
          <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#fff', background: latestCfg.border,
            borderRadius: '10px', padding: '1px 5px', flexShrink: 0 }}>
            {badgeCount}
          </span>
        )}
        {latestToast && (
          <button onClick={(e) => { e.stopPropagation(); onDismiss(latestToast.id); }} aria-label="Dismiss notification" title="Dismiss notification"
            style={{ background: 'none', border: 'none', color: latestCfg.color, cursor: 'pointer',
                     opacity: 0.5, padding: 0, lineHeight: 1, flexShrink: 0, display: 'flex' }}>
            <X size={11} strokeWidth={2} />
          </button>
        )}
        {(history.length > 0 || toasts.length > 1) && (
          <span style={{ flexShrink: 0, display: 'flex', color: latestCfg.color, opacity: 0.5 }}>
            {showHistory ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </span>
        )}
      </div>

      {/* Dropdown — all active toasts + history */}
      {showHistory && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: '8px',
          background: '#1a1a1a', border: '1px solid #333', borderRadius: '10px',
          width: '400px', maxHeight: '360px', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: '8px', zIndex: Z.dropdown,
        }}>
          {/* Active toasts */}
          {toasts.length > 0 && (
            <>
              <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 4px 4px' }}>
                Active ({toasts.length})
              </div>
              {toasts.map(t => {
                const cfg = cfgFor(t.type);
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px',
                    borderRadius: '6px', marginBottom: '3px', background: cfg.bg, border: `1px solid ${cfg.border}40` }}>
                    <span style={{ flexShrink: 0, display: 'flex' }}>
                      {t.type === 'running'
                        ? <Loader size={11} strokeWidth={2} style={{ color: cfg.color, animation: 'spin 1s linear infinite' }} />
                        : t.type === 'success'
                          ? <Check size={11} strokeWidth={2.5} style={{ color: cfg.color }} />
                          : t.type === 'error'
                            ? <AlertTriangle size={11} strokeWidth={2} style={{ color: cfg.color }} />
                            : <Clock size={11} style={{ color: cfg.color }} />}
                    </span>
                    <span style={{ flex: 1, fontSize: '0.68rem', color: cfg.color, lineHeight: 1.3 }}>
                      {t.message}
                    </span>
                    {t.startedAt && <ToastDuration startedAt={t.startedAt} endedAt={t.endedAt ?? null} />}
                    <button onClick={() => onDismiss(t.id)} aria-label="Dismiss notification" title="Dismiss notification"
                      style={{ background: 'none', border: 'none', color: cfg.color, cursor: 'pointer',
                               opacity: 0.5, padding: 0, lineHeight: 1, flexShrink: 0 }}>
                      <X size={11} strokeWidth={2} />
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {/* History */}
          {history.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '4px 4px 4px', marginTop: toasts.length ? '6px' : 0 }}>
                <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  History ({history.length})
                </span>
                {onClearHistory && (
                  <button onClick={onClearHistory} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.58rem' }}>
                    <Trash2 size={9} /> Clear
                  </button>
                )}
              </div>
              {history.map((t, i) => {
                const cfg = cfgFor(t.type);
                return (
                  <div key={`${t.id}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 6px',
                    borderRadius: '6px', marginBottom: '2px', background: `${cfg.bg}88` }}>
                    <span style={{ flexShrink: 0, display: 'flex' }}>
                      {t.type === 'success' ? <Check size={10} style={{ color: cfg.color }} />
                        : t.type === 'error' ? <AlertTriangle size={10} style={{ color: cfg.color }} />
                        : <Clock size={10} style={{ color: cfg.color }} />}
                    </span>
                    <span style={{ flex: 1, fontSize: '0.68rem', color: cfg.color, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.message}
                    </span>
                    <span style={{ fontSize: '0.55rem', color: '#666', fontFamily: 'monospace', flexShrink: 0 }}>
                      {fmtTime(t.endedAt || t.startedAt)}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>,
    anchor
  );
}
